import { useState, useEffect } from "react";
import { AdminContainer, AlignedListContainer } from "../components/layouts";
import api from "../api";
import { formatMoney } from "../lithic/Money";
import Link from "../components/Link";
import { GutterlessListItem as ListItem } from "../components/lists";
import {
  ListItemText,
  Button,
  Box,
  Typography,
  Chip,
  CircularProgress
} from "@material-ui/core";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import ConfirmationDialog from "../components/ConfirmationDialog";
import StatementsTable from "../components/StatementsTable";

export default function AggregatedPaymentDetailPage() {
  const { id } = useParams();
  const [ap, setAp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);

  useEffect(() => {
    setLoading(true);
    api
      .getAggregatedPayment({ id })
      .then(api.pickData)
      .then(setAp)
      .catch(() => toast.error("Failed to load aggregated payment"))
      .finally(() => setLoading(false));
  }, [id]);

  const handleConfirm = () => {
    setConfirmDialog({
      title: "Confirm Aggregated Payment",
      text: "This will process the payment for ACH transfer. Continue?",
      onConfirm: () => {
        setActionLoading(true);
        api
          .confirmAggregatedPayment({ id })
          .then(api.pickData)
          .then((data) => {
            setAp(data);
            toast.success("Payment confirmed and processed");
          })
          .catch((err) =>
            toast.error(err.response?.data?.message || "Confirm failed")
          )
          .finally(() => {
            setActionLoading(false);
            setConfirmDialog(null);
          });
      }
    });
  };

  const handleCancel = () => {
    setConfirmDialog({
      title: "Cancel Aggregated Payment",
      text: "This will cancel the aggregated payment. The statements will become available for re-aggregation. Continue?",
      onConfirm: () => {
        setActionLoading(true);
        api
          .cancelAggregatedPayment({ id })
          .then(api.pickData)
          .then((data) => {
            setAp(data);
            toast.success("Payment canceled");
          })
          .catch((err) =>
            toast.error(err.response?.data?.message || "Cancel failed")
          )
          .finally(() => {
            setActionLoading(false);
            setConfirmDialog(null);
          });
      }
    });
  };

  const handleRetry = () => {
    setConfirmDialog({
      title: "Retry Aggregated Payment",
      text: "This will retry processing the failed payment for ACH transfer. Continue?",
      onConfirm: () => {
        setActionLoading(true);
        api
          .retryAggregatedPayment({ id })
          .then(api.pickData)
          .then((data) => {
            setAp(data);
            toast.success("Payment retry processed");
          })
          .catch((err) =>
            toast.error(err.response?.data?.message || "Retry failed")
          )
          .finally(() => {
            setActionLoading(false);
            setConfirmDialog(null);
          });
      }
    });
  };

  if (loading) {
    return (
      <AdminContainer variant="detail">
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      </AdminContainer>
    );
  }

  if (!ap) {
    return (
      <AdminContainer variant="detail">
        <Typography>Aggregated payment not found.</Typography>
      </AdminContainer>
    );
  }

  const getStatusColor = () => {
    if (ap.status === "failed") return "secondary";
    if (ap.status === "processed" || ap.status === "confirmed") return "primary";
    return "default";
  };
  const statusColor = getStatusColor();

  const showConfirm = ap.status === "draft";
  const showCancel = ap.status === "draft" || ap.status === "confirmed";
  const showRetry = ap.status === "failed";

  return (
    <AdminContainer variant="detail">
      <AlignedListContainer maxWidth="sm">
        <ListItem>
          <ListItemText>
            <Typography variant="h6">
              Aggregated Payment #{ap.id}
            </Typography>
          </ListItemText>
        </ListItem>

        <ListItem>
          <ListItemText>
            Status:{" "}
            <Chip
              size="small"
              label={ap.status}
              color={statusColor}
              variant={
                ap.status === "canceled" || ap.status === "draft"
                  ? "outlined"
                  : "default"
              }
            />
          </ListItemText>
        </ListItem>

        <ListItem>
          <ListItemText>
            Type:{" "}
            {ap.payType === "pay_in" ? "Invoice (Pay In)" : "Payout (Pay Out)"}
          </ListItemText>
        </ListItem>

        <ListItem>
          <ListItemText>
            Total: {formatMoney(ap.totalAmount)}
          </ListItemText>
        </ListItem>

        <ListItem>
          <ListItemText>
            Partner:{" "}
            <Link to={`/partners/${ap.partnerId}`}>
              Partner {ap.partnerId}
            </Link>
          </ListItemText>
        </ListItem>

        {ap.bankAccount && (
          <ListItem>
            <ListItemText>
              Bank Account:{" "}
              <Link to={`/bank-accounts/${ap.bankAccount.id}`}>
                {ap.bankAccount.name || "N/A"}{" "}
                {ap.bankAccount.last4 ? `(****${ap.bankAccount.last4})` : ""}
              </Link>
            </ListItemText>
          </ListItem>
        )}

        {ap.paymentId && (
          <ListItem>
            <ListItemText>
              Payment:{" "}
              <Link
                to={
                  ap.paymentType === "Invoice"
                    ? `/invoices/${ap.paymentId}`
                    : `/payouts/${ap.paymentId}`
                }
              >
                {ap.paymentType} #{ap.paymentId}
              </Link>
            </ListItemText>
          </ListItem>
        )}

        {ap.memo && (
          <ListItem>
            <ListItemText>Memo: {ap.memo}</ListItemText>
          </ListItem>
        )}

        {ap.increaseAchTransferId && (
          <ListItem>
            <ListItemText>
              ACH Transfer ID: {ap.increaseAchTransferId}
            </ListItemText>
          </ListItem>
        )}

        {ap.increaseAchTransferStatus && (
          <ListItem>
            <ListItemText>
              ACH Transfer Status: {ap.increaseAchTransferStatus}
            </ListItemText>
          </ListItem>
        )}

        {ap.errorMessage && (
          <ListItem>
            <ListItemText>
              <Typography color="error">
                Error: {ap.errorMessage}
              </Typography>
            </ListItemText>
          </ListItem>
        )}

        <ListItem>
          <ListItemText>
            Created:{" "}
            {ap.createdAt
              ? dayjs(ap.createdAt).format("MMM D, YYYY HH:mm:ss")
              : "-"}
          </ListItemText>
        </ListItem>

        <ListItem>
          <ListItemText>
            Updated:{" "}
            {ap.updatedAt
              ? dayjs(ap.updatedAt).format("MMM D, YYYY HH:mm:ss")
              : "-"}
          </ListItemText>
        </ListItem>

        <ListItem>
          <ListItemText>
            Idempotency Key: {ap.idempotencyKey || "-"}
          </ListItemText>
        </ListItem>

        {/* Action Buttons */}
        {(showConfirm || showCancel || showRetry) && (
          <Box mt={2} mb={2} display="flex" style={{ gap: 8 }}>
            {showConfirm && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleConfirm}
                disabled={actionLoading}
              >
                Confirm & Process
              </Button>
            )}
            {showRetry && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleRetry}
                disabled={actionLoading}
              >
                Retry Processing
              </Button>
            )}
            {showCancel && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleCancel}
                disabled={actionLoading}
              >
                Cancel
              </Button>
            )}
          </Box>
        )}
      </AlignedListContainer>

      {/* Statements Table */}
      {ap.statements && ap.statements.length > 0 && (
        <Box mt={3}>
          <Typography variant="h6" gutterBottom>
            Included Statements ({ap.statements.length})
          </Typography>
          <StatementsTable
            statements={ap.statements}
            statementAmounts={ap.statementAmounts}
            linkStatements
          />
        </Box>
      )}

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <ConfirmationDialog
          open={!!confirmDialog}
          title={confirmDialog.title}
          text={confirmDialog.text}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
          cancelProps={{ children: "No, go back" }}
          confirmProps={{
            children: actionLoading ? (
              <CircularProgress size={20} />
            ) : (
              "Yes, proceed"
            ),
            disabled: actionLoading
          }}
        />
      )}
    </AdminContainer>
  );
}
